import { IBaseModel } from '../base/base-model.interface';
import { createEnumOptions } from '../shared/enum-option.interface';
import { IOltConstraint } from './olt-constraint.interface';

export interface IOlt extends IBaseModel<'OLT'> {
  name: string;
  sn?: string;
  softwareVersion?: string;
  productName?: string;
  gpsX?: number | null;
  gpsY?: number | null;
  tinyControll?: {
    hwEnvFanStatus: string;
    hwEnvFanSpeed: string;
    hwEnvFanName: string;
    hwEnvFanCurrentTemp: string;
  };
  imco?:
    | {
        powerLoad: string;
        outputVoltage: string;
        power: string;
      }
    | any;
  eic?: string | null;
  submitted?: boolean;
  spConstraint?: IOltConstraint;
  oltCapabilities?: OltCapabilities;
}

export enum OltCapabilities {
  gpon = 1,
  xgspon = 2,
}

export const oltCapabilitiesOptions = createEnumOptions({
  [OltCapabilities.gpon]: { label: 'GPON' },
  [OltCapabilities.xgspon]: { label: 'XGS-PON' },
  _default: { label: '-' },
});

export enum OltOperateState {
  unknown = 0,
  available = 1,
  unavailable = 2,
}

export enum ZabbixOltKey {
  systemVersion = 'system.version',
  systemUptime = 'system.uptime',
  systemName = 'system.name',
  systemLocation = 'system.location',
  systemContact = 'system.contact',
  sysVersion = 'sys.version',
  sysUptime = 'sys.uptime',
  sysName = 'sys.name',
  sysLocation = 'sys.location',
  sysContact = 'sys.contact',
  slotCpuLoadValue = 'slot.cpuload.value',
  slotTempValue = 'slot.temp.value',
  slotStateValue = 'slot.state.value',
  slotHwMusaBoardTemperature = 'slot.hwMusaBoardTemperature.',
  slotHwMusaBoardSlotDesc = 'slot.hwMusaBoardSlotDesc.',
  slotHwMusaBoardRamUse = 'slot.hwMusaBoardRamUse.',
  slotHwMusaBoardCpuRate = 'slot.hwMusaBoardCpuRate.',
  sensorTempValue = 'sensor.temp.value',
  oltHwGponDeviceOltControlOntNum = 'olt.hwGponDeviceOltControlOntNum.',
  icmppingsec = 'icmppingsec',
  icmppingloss = 'icmppingloss',
  icmpping = 'icmpping',
  hwEnvFanStatus = 'hwEnv.fan.status',
  hwEnvFanSpeed = 'hwEnv.fan.speed',
  hwEnvFanName = 'hwEnv.fan.name',
  hwEnvFanCurrentTemp = 'hwEnv.fan.currentTemp',
  boardTypeValue = 'board.type.value',
  boardCpuLoadValue = 'board.cpuload.value',
  uplinkAllOut = 'Uplink_all_out',
  uplinkAllIn = 'Uplink_all_in',
  uplinkIfWavelenght = 'Uplink.ifWavelenght.',
  uplinkIfVendorSn = 'Uplink.ifVendorSn.',
  uplinkIfVendorPn = 'Uplink.ifVendorPn.',
  uplinkIfVendorName = 'Uplink.ifVendorName.',
  uplinkIfTemperature = 'Uplink.ifTemperature.',
  uplinkIfSupplyVoltageMinThreshold = 'Uplink.ifSupplyVoltageMinThreshold.',
  uplinkIfSupplyVoltageMaxThreshold = 'Uplink.ifSupplyVoltageMaxThreshold.',
  uplinkIfSupplyVoltage = 'Uplink.ifSupplyVoltage.',
  uplinkIfSpeedUplink = 'Uplink.ifSpeedUplink.',
  uplinkIfSpeed = 'Uplink.ifSpeed.',
  uplinkIfRxPowerThresholdMin = 'Uplink.ifRxPowerThresholdMin.',
  uplinkIfRxPower = 'Uplink.ifRxPower.',
  uplinkIfTxPower = 'Uplink.ifTxPower.',
  uplinkIfOutErrorsPkts = 'Uplink.ifOutErrorsPkts.',
  uplinkIfOutErrors = 'Uplink.ifOutErrors.',
  uplinkIfOutDiscardsPkts = 'Uplink.ifOutDiscardsPkts.',
  uplinkIfOutDiscards = 'Uplink.ifOutDiscards.',
  uplinkIfOperStatus = 'Uplink.ifOperStatus.',
  uplinkIfMinTxPowerThreshold = 'Uplink.ifMinTxPowerThreshold.',
  uplinkIfMinTemperatureThreshold = 'Uplink.ifMinTemperatureThreshold.',
  uplinkIfMinRxPowerThreshold = 'Uplink.ifMinRxPowerThreshold.',
  uplinkIfMaxTxPowerThreshold = 'Uplink.ifMaxTxPowerThreshold.',
  uplinkIfMaxTemperatureThreshold = 'Uplink.ifMaxTemperatureThreshold.',
  uplinkIfMaxRxPowerThreshold = 'Uplink.ifMaxRxPowerThreshold.',
  uplinkIfInErrorsPkts = 'Uplink.ifInErrorsPkts.',
  uplinkIfInErrors = 'Uplink.ifInErrors.',
  uplinkIfInDiscardsPkts = 'Uplink.ifInDiscardsPkts.',
  uplinkIfInDiscards = 'Uplink.ifInDiscards.',
  uplinkIfHCOutOctets = 'Uplink.ifHCOutOctets.',
  uplinkIfHCOutMcast = 'Uplink.ifHCOutMcast.',
  uplinkIfHCOutBroadcastPkts = 'Uplink.ifHCOutBroadcastPkts.',
  uplinkIfHCInOctets = 'Uplink.ifHCInOctets.',
  uplinkIfHCInMcast = 'Uplink.ifHCInMcast.',
  uplinkIfHCInBroadcastPkts = 'Uplink.ifHCInBroadcastPkts.',
  uplinkIfDistance = 'Uplink.ifDistance.',
  uplinkIfBiasCurrentMinThreshold = 'Uplink.ifBiasCurrentMinThreshold.',
  uplinkIfBiasCurrentMaxThreshold = 'Uplink.ifBiasCurrentMaxThreshold.',
  uplinkIfBiasCurrent = 'Uplink.ifBiasCurrent.',
  uplinkIfAlias = 'Uplink.ifAlias.',
  ponIfVoltage = 'Pon.ifVoltage.',
  ponIfTxPower = 'Pon.ifTxPower.',
  ponIfTemperature = 'Pon.ifTemperature.',
  ponIfOutErrorsPkts = 'Pon.ifOutErrorsPkts.',
  ponIfOutError = 'Pon.ifOutError.',
  ponIfOutDicardsPkts = 'Pon.ifOutDicardsPkts.',
  ponIfOperStatus = 'Pon.ifOperStatus.',
  ponIfInErrorsPkts = 'Pon.ifInErrorsPkts.',
  ponIfInDiscardsPkts = 'Pon.ifInDiscardsPkts.',
  ponIfHCOutOctetsPon = 'Pon.ifHCOutOctetsPon.',
  ponIfHCOutOctets = 'Pon.ifHCOutOctets.',
  ponIfHCOutBcastPkts = 'Pon.ifHCOutBcastPkts.',
  ponIfHCOutBcast = 'Pon.ifHCOutBcast.',
  ponIfHCInOctets = 'Pon.ifHCInOctets.',
  ponIfHCInBcastPkts = 'Pon.ifHCInBcastPkts.',
  ponIfHCInBcast = 'Pon.ifHCInBcast.',
  ponIfFecStatus = 'Pon.ifFecStatus.',
  ponIfAutoFindStatus = 'Pon.ifAutoFindStatus.',
  ponIfBias = 'Pon.ifBias.',
  ponHwGponDeviceOltControlOntNumOnline = 'Pon.hwGponDeviceOltControlOntNumOnline.',
  ponHwGponDeviceOltControlOntNum = 'Pon.hwGponDeviceOltControlOntNum.',
  ponIfOutDiscards = 'Pon.IfOutDiscards.',
  fanBoardStatus = 'Fan.boardStatus.',
  fanBoardPower = 'Fan.boardPower.',
  fanBoardCommState = 'Fan.boardCommState.',
  fanFanSpeed = 'Fan.FanSpeed.',
  // ICMP
  imcoPowerLoad = 'power.load',
  imcoOutputVoltage = 'output.voltage1',
  imcoInputPower = 'input.power',
  batteryChargeState = 'battery.charge.state',
  genWorking = 'gen.working',
  backupTime = 'backup.time',
  middlePoint = 'middle.point',
  // lk3
  lk3ModbusVoltage = 'modbus_voltage',
  lk3ModbusActivePower = 'modbus_active_power',
  lk3Temperature = 'temperature',
  lk3Cooling = 'output_1',
  lk3Door = 'input_digital_1',
  // capacity test
  battcapExtracted = 'battcap.extracted',
  battcapDatum = 'battcap.datum',
  battcapStatus = 'battcap.status',
  battcapVcut = 'battcap.vcut',
  battcapIavg = 'battcap.iavg',
  battcapTime = 'battcap.time',
  battcapTavg = 'battcap.tavg',
}

export const zabbixOltItemKeys = new Map<ZabbixOltKey, string>(
  Object.entries(ZabbixOltKey).map(([key, value]) => [
    value as ZabbixOltKey,
    key,
  ]),
);

export interface ZabbixImco {
  imcoPowerLoad?: string;
  imcoOutputVoltage?: string;
  imcoInputPower?: string;
  batteryChargeState?: string;
  genWorking?: string;
  backupTime?: string;
  middlePoint?: string;
}

export interface ZabbixHost {
  name?: string;
  os?: string;
  software?: string;
  location?: string;
  gpsX?: number;
  gpsY?: number;
  model?: string;
  vendor?: string;
  city?: string;
  country?: string;
  zip?: string;
  hostid?: string;
  lk3Door?: string;
  imco?: ZabbixImco;
}

export interface UplinkDetail {
  fsp: string;
  temperature: number;
}

export interface GponDetail {
  fsp: string;
  temperature: number;
}

export interface UplinkPort {
  fsp: string;
  uplinkIfAlias: string;
  groupId?: string;
}

export interface IOntUplinkPort {
  fsp: string;
  uplinkIfAlias?: string;
  uplinkIfOperStatus?: number | string;
  uplinkIfSpeed?: number;
  uplinkIfRxPower?: number;
  uplinkIfTxPower?: number;
  uplinkIfVendorPn?: string;
}

export interface IMasterUplinkPort {
  id: string;
  alias?: string;
  frame: string;
  slot: string;
  port: string;
  fsp: string;
  operateState?: PortState;
  speed?: number;
}

export enum PortState {
  up = 1,
  down = 0,
}

export const imasterPortsStateOptions = createEnumOptions({
  0: { label: 'online', color: 'success' },
  _default: {
    label: 'offline',
    color: 'danger',
  },
});

export const zabbixPortsStateOptions = createEnumOptions({
  0: { label: 'offline', color: 'danger' },
  1: { label: 'online', color: 'success' },
  _default: { label: '-' },
});

export const oltOperateStateOptions = createEnumOptions({
  [OltOperateState.available]: { label: 'Dostupné', color: 'success' },
  [OltOperateState.unavailable]: { label: 'Nedostupné', color: 'danger' },
  [OltOperateState.unknown]: { label: 'Neznáme' },
  _default: { label: '-' },
});

export enum OltEmailSubType {
  oltAssignedSp = 'oltAssignedSp',
  oltAllowedIsp = 'oltAllowedIsp',
  oltSubmitted = 'oltSubmitted',
}

export const oltEmailSubTypeOptions = createEnumOptions<OltEmailSubType>({
  [OltEmailSubType.oltAssignedSp]: { label: 'OLT priradené' },
  [OltEmailSubType.oltAllowedIsp]: { label: 'OLT povolené na objednávanie' },
  [OltEmailSubType.oltSubmitted]: { label: 'OLT odovzdané' },
  _default: { label: '-' },
});

export interface ICapacityTestDetails {
  battcapDatum?: string;
  battcapExtracted?: string;
  battcapIavg?: string;
  battcapStatus?: string;
  battcapTime?: string;
  battcapTavg?: string;
  battcapVcut?: string;
}
